import React, { useCallback, useContext, useMemo } from "react";
import { Helmet } from "react-helmet-async";
import "./kyb.css";
import { ProfileContext } from "../../../context/ProfileProvider";
import { useKybStatusRouting } from "./useKybStatus";
import { useKybVerifyAction } from "./kybCtaHelpers";

export default function KYBVerification({ userDetails: userDetailsProp = {} }) {
  const [openFaq, setOpenFaq] = React.useState(null);
  const profileCtx = useContext(ProfileContext) || {};
  const handleUserDetails = profileCtx.handleUserDetails;

  const {
    kybStatusRes,
    canonicalStatus,
    diditStatus,
    hasResponse,
    loadKybStatus,
  } = useKybStatusRouting();

  const onFlowEnd = useCallback(() => {
    void loadKybStatus();
    handleUserDetails?.();
  }, [loadKybStatus, handleUserDetails]);

  const { busy, cta, startVerify } = useKybVerifyAction({
    kybStatusRes,
    canonicalStatus,
    diditStatus,
    onFlowEnd,
    userDetails: userDetailsProp,
  });

  const isResubmit = canonicalStatus === "RESUBMISSION_REQUESTED";
  const isInProgress = diditStatus === "In Progress";
  const isPendingReview = canonicalStatus === "PENDING" && !isInProgress;

  const statusHeadline = useMemo(() => {
    if (isResubmit) return "Resubmission required";
    if (isInProgress) return "Verification In Progress";
    if (isPendingReview) return "Verification In Review";
    return null;
  }, [isResubmit, isInProgress, isPendingReview]);

  const statusCopy = useMemo(() => {
    if (isResubmit) {
      return "We need you to run business verification again. Complete the steps in the flow and submit the required documents.";
    }
    if (isInProgress) {
      return "Your KYB verification session is still open. Resume it to complete the remaining steps.";
    }
    if (isPendingReview) {
      return "Your business verification is being processed and is currently under review. We'll notify you once it's approved or if any additional information is required.";
    }
    return null;
  }, [isResubmit, isInProgress, isPendingReview]);

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  return (
    <div className="dashboard_right">
      <Helmet>
        <meta
          httpEquiv="Permissions-Policy"
          content="camera=*, microphone=*, display-capture=*, fullscreen=*, publickey-credentials-get=*"
        />
      </Helmet>
      <div className={`kyc-page kyb_verification_page${!hasResponse ? " kyb-status-loading" : ""}`}>

        <div className="kyb_page_header">
          <h1>KYB Verification Center</h1>
          <p>Verify your business identity</p>
        </div>
        <div className="kyb_start_layout">
          <div className="kyb_start_left">

            <div className="kyb_box kyb_hero_box">
              <div className="kyb_hero_content">
                <h2>Business Verification (KYB)</h2>
                {statusHeadline ? (
                  <div className="kyb_status_banner">
                    <div className="kyb_status_banner_title">{statusHeadline}</div>
                    <p className="kyb_status_banner_text">{statusCopy}</p>
                    {diditStatus ? (
                      <span className="kyb_status_vendor">Status: {diditStatus}</span>
                    ) : null}
                  </div>
                ) : (
                  <p>Verify your business information securely and efficiently.</p>
                )}

                <ul className="kyb_hero_list">
                  <li><i className="ri-check-line"></i> Global compliance standards</li>
                  <li><i className="ri-check-line"></i> Secure data encryption</li>
                  <li><i className="ri-check-line"></i> Faster verification process</li>
                </ul>

                {/* KYC-style: hide CTA while under review (PENDING + not In Progress) */}
                <button
                  type="button"
                  className={cta.show ? `kyb_start_btn${cta.variant === "continue" ? " kyb_continue_cta" : ""}${cta.variant === "resubmit" ? " kyb_resubmit_cta" : ""}` : "kyb_start_btn"}
                  onClick={() => void startVerify()}
                  disabled={busy || !hasResponse}
                  aria-busy={busy}
                >
                  {busy ? "Starting…" : cta.label}
                </button>
              </div>
              <div className="kyb_hero_img">
                <img className="dark_img" src="/images/kyb-verification-img/kyb_verification_img.svg" alt="KYB Verification" style={{ width: '100%' }} />
                <img className="light_img" src="/images/kyb-verification-img/kyb_verification_img_light.svg" alt="KYB Verification" style={{ width: '100%' }} />
              </div>
            </div>

            <div className="kyb_box kyb_docs_box">
              <h3>Required Documents</h3>
              <p>Please prepare the following documents before starting verification.</p>

              <div className="kyb_docs_grid">
                <div className="kyb_doc_card">
                  <div className="kyb_doc_icon">
                    <img src="/images/kyb-verification-img/kyb_document_icon.svg" alt="Doc" />
                  </div>
                  <div className="kyb_doc_info">
                    <h4>Certificate of Incorporation</h4>
                    <p>Business incorporation certificate</p>
                  </div>
                </div>

                <div className="kyb_doc_card">
                  <div className="kyb_doc_icon">
                    <img src="/images/kyb-verification-img/kyb_document_icon2.svg" alt="Doc" />
                  </div>
                  <div className="kyb_doc_info">
                    <h4>Business Registration Document</h4>
                    <p>Official business registration certificate</p>
                  </div>
                </div>

                <div className="kyb_doc_card">
                  <div className="kyb_doc_icon">
                    <img src="/images/kyb-verification-img/kyb_document_icon3.svg" alt="Doc" />
                  </div>
                  <div className="kyb_doc_info">
                    <h4>Beneficial Owner Information</h4>
                    <p>Details of ultimate beneficial owners (UBO)</p>
                  </div>
                </div>

                <div className="kyb_doc_card">
                  <div className="kyb_doc_icon">
                    <img src="/images/kyb-verification-img/kyb_document_icon4.svg" alt="Doc" />
                  </div>
                  <div className="kyb_doc_info">
                    <h4>Company Structure / Ownership Proof</h4>
                    <p>Shareholding structure and ownership proof</p>
                  </div>
                </div>

                <div className="kyb_doc_card">
                  <div className="kyb_doc_icon">
                    <img src="/images/kyb-verification-img/kyb_document_icon.svg" alt="Doc" />
                  </div>
                  <div className="kyb_doc_info">
                    <h4>Board Resolution (If Applicable)</h4>
                    <p>Board resolution authorizing account opening</p>
                  </div>
                </div>

                <div className="kyb_doc_card">
                  <div className="kyb_doc_icon">
                    <img src="/images/kyb-verification-img/kyb_document_icon2.svg" alt="Doc" />
                  </div>
                  <div className="kyb_doc_info">
                    <h4>Proof of Business Address</h4>
                    <p>Recent utility bill or bank statement</p>
                  </div>
                </div>

              </div>
            </div>

          </div>

          <div className="kyb_start_right">
            <div className="kyb_box kyb_faq_box">
              <h3>Frequently Asked Questions</h3>
              <div className="kyb_faq_list">

                <div className={`kyb_faq_item ${openFaq === 1 ? 'active' : ''}`}>
                  <div className="kyb_faq_question" onClick={() => toggleFaq(1)}>
                    What is KYB verification?
                    <i className="ri-arrow-down-s-line"></i>
                  </div>
                  <div className="kyb_faq_answer">
                    KYB verification on AGCX confirms that a business is legitimate and authorized to open an institutional or corporate account.
                  </div>
                </div>

                <div className={`kyb_faq_item ${openFaq === 2 ? 'active' : ''}`}>
                  <div className="kyb_faq_question" onClick={() => toggleFaq(2)}>
                    Why is KYB verification required?
                    <i className="ri-arrow-down-s-line"></i>
                  </div>
                  <div className="kyb_faq_answer">
                    KYB is required on AGCX to meet UAE regulatory standards and to help prevent fraud, money laundering, and illicit activity.
                  </div>
                </div>

                <div className={`kyb_faq_item ${openFaq === 3 ? 'active' : ''}`}>
                  <div className="kyb_faq_question" onClick={() => toggleFaq(3)}>
                    What documents are required for KYB verification?
                    <i className="ri-arrow-down-s-line"></i>
                  </div>
                  <div className="kyb_faq_answer">
                    You will need incorporation certificates, UBO details, proof of address, and other documents requested in the AGCX KYB flow.
                  </div>
                </div>

                <div className={`kyb_faq_item ${openFaq === 4 ? 'active' : ''}`}>
                  <div className="kyb_faq_question" onClick={() => toggleFaq(4)}>
                    How long does KYB verification take?
                    <i className="ri-arrow-down-s-line"></i>
                  </div>
                  <div className="kyb_faq_answer">
                    Typically 1–3 business days after all correct documents are submitted through AGCX. Status updates appear on your KYB page.
                  </div>
                </div>

                <div className={`kyb_faq_item ${openFaq === 5 ? 'active' : ''}`}>
                  <div className="kyb_faq_question" onClick={() => toggleFaq(5)}>
                    Can I update my business information after verification?
                    <i className="ri-arrow-down-s-line"></i>
                  </div>
                  <div className="kyb_faq_answer">
                    Yes. Contact AGCX support at support@arabglobal.ae to update your business details. Additional verification may be required.
                  </div>
                </div>

                <div className={`kyb_faq_item ${openFaq === 6 ? 'active' : ''}`}>
                  <div className="kyb_faq_question" onClick={() => toggleFaq(6)}>
                    Is my business information safe and secure?
                    <i className="ri-arrow-down-s-line"></i>
                  </div>
                  <div className="kyb_faq_answer">
                    Yes. AGCX encrypts business data and handles it in line with our security and privacy standards.
                  </div>
                </div>

                <div className={`kyb_faq_item ${openFaq === 7 ? 'active' : ''}`}>
                  <div className="kyb_faq_question" onClick={() => toggleFaq(7)}>
                    What happens if my KYB verification is rejected?
                    <i className="ri-arrow-down-s-line"></i>
                  </div>
                  <div className="kyb_faq_answer">
                    AGCX will notify you with the reason. You can resubmit the required documents from the KYB page or open a ticket at support@arabglobal.ae.
                  </div>
                </div>

              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}
